import React, { useState, useEffect } from 'react';
import ReactModal from 'react-modal';
import { generateProgramCsv } from '../../api/admin';
import './RosterExportModal.css';

const ProgramExportModal = ({ programIds, onClose, onSuccess }) => {
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const doExport = async () => {
      try {
        const response = await generateProgramCsv(programIds);
        if (response.status === 'success') {
          setResult(response.data);
          setStatus('success');
          if (onSuccess) onSuccess(programIds);
        } else {
          setErrorMessage(response.message || 'Failed to generate program CSV');
          setStatus('error');
        }
      } catch (error) {
        setErrorMessage(error.message || 'An unexpected error occurred');
        setStatus('error');
      }
    };
    doExport();
  }, [programIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRetry = () => {
    setStatus('loading');
    setErrorMessage('');
    setResult(null);
    const doExport = async () => {
      try {
        const response = await generateProgramCsv(programIds);
        if (response.status === 'success') {
          setResult(response.data);
          setStatus('success');
          if (onSuccess) onSuccess(programIds);
        } else {
          setErrorMessage(response.message || 'Failed to generate program CSV');
          setStatus('error');
        }
      } catch (error) {
        setErrorMessage(error.message || 'An unexpected error occurred');
        setStatus('error');
      }
    };
    doExport();
  };

  const renderLoading = () => (
    <div className="roster-export-content">
      <div className="loading-spinner"></div>
      <h3>Generating CSV...</h3>
      <p>Please wait while we generate the program CSV for {programIds.length} program{programIds.length > 1 ? 's' : ''}.</p>
    </div>
  );

  const downloadCsvFile = (filename, base64Content) => {
    // Decode base64 to binary
    const binaryString = atob(base64Content);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownload = () => {
    if (result.fileContent) {
      downloadCsvFile(result.filename, result.fileContent);
    } else if (result.downloadUrl) {
      window.open(result.downloadUrl, '_blank');
    }
  };

  const renderSuccess = () => {
    const hasLocalContent = !!result.fileContent;

    return (
      <div className="roster-export-content">
        <div className="success-icon">&#10003;</div>
        <h3>Program CSV Generated Successfully</h3>

        <div className="result-details">
          <p><strong>File:</strong> {result.filename}</p>
          <p><strong>Programs:</strong> {result.programCount}</p>
        </div>

        <div className="modal-actions">
          {hasLocalContent ? (
            <button className="primary-button" onClick={handleDownload}>
              Download CSV
            </button>
          ) : (
            <a
              href={result.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="primary-button"
            >
              Download {result.filename}
            </a>
          )}
          <button className="secondary-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  };

  const renderError = () => (
    <div className="roster-export-content">
      <div className="error-icon">&#10007;</div>
      <h3>Export Failed</h3>
      <p className="error-message">{errorMessage}</p>
      <div className="modal-actions">
        <button className="primary-button" onClick={handleRetry}>
          Retry
        </button>
        <button className="secondary-button" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );

  return (
    <ReactModal
      isOpen={true}
      onRequestClose={status !== 'loading' ? onClose : undefined}
      shouldCloseOnOverlayClick={status !== 'loading'}
      contentLabel="Program Export"
      ariaHideApp={false}
      className="roster-export-modal-content"
      overlayClassName="modal-overlay"
    >
      <div className="roster-export-modal">
        {status !== 'loading' && (
          <button className="close-button" onClick={onClose}>x</button>
        )}
        {status === 'loading' && renderLoading()}
        {status === 'success' && renderSuccess()}
        {status === 'error' && renderError()}
      </div>
    </ReactModal>
  );
};

export default ProgramExportModal;
